import {
    documentEventDispatcher,
    EVENT_ADD_TO_CART,
    EVENT_SHOW_MINI_CART, EVENT_SHOW_QUANTITY_UPSELL
} from "./eggo-cart-events";
import {hideDomElement} from "../helpers";
import {registerProductCardCartColors, buttonAddToCart} from "./product-card";
import {EggoCartDiscountLogic} from "./eggo-cart-logic";
import {EggoCartShipping} from "./eggo-cart-shipping";
import {checkOrderLimit} from "./eggo-cart-limit";
import {addToCart, removeItemCart} from "./mini-cart";

/** If a cart request has not finished by then, give up on the drawer and send the customer to the cart page. */
const CART_REQUEST_TIMEOUT = 10000;

export class EggoCart {
    constructor() {
        this.cartDrawer = null;
        this.cartCache = null;
        this.eggoCartRealWrapper = document.querySelector(".cart-notification-wrapper");
        this.registerAddToCartGlobalEvent();
        this.registerOpenMiniCartEvent();
        this.timeout = null;
        /** Cart requests run one after another so a slow older response can never overwrite a newer one. */
        this.requestQueue = Promise.resolve();
    }

    init(content) {
        this.eggoCartWrapper = document.createElement("div");
        this.eggoCartWrapper.innerHTML = content ? content : this.getCartContent();
        this.eggoCart = this.eggoCartWrapper.querySelector(".cart-notification-inner-wrapper");

        if (this.cartCache) {
            this.activateFakeCart(false);
        }

        this.registerInitialEvents();
    }

    setWrapperLoading() {
        this.eggoCartRealWrapper.classList.add("loading");
    }

    unsetWrapperLoading() {
        this.eggoCartRealWrapper.classList.remove("loading");
    }

    getCart(url = `${routes.cart_update_url}.js`, body = {updates: {}}) {
        this.setWrapperLoading();
        this.cartRequest(url, body);
    }

    refreshCart() {
        const mainCartWrapper = document.querySelector(".cart-notification-wrapper");
        mainCartWrapper.innerHTML = "";
        mainCartWrapper.append(this.eggoCart);
    }

    getCartContent() {
        return document.querySelector(".cart-notification-inner-wrapper").outerHTML;
    }

    registerInitialEvents() {
        this.registerMiniCartCloseEvents();
        this.registerLineItemQuantityChangeEvent();
        this.registerLineItemRemoveEvent();
        this.registerCouponEvents();
        this.registerAddToCartGifts();
        registerProductCardCartColors(this.eggoCart.querySelector(".cart-drawer__inner-real"));
    }

    registerMiniCartCloseEvents() {
        /** Close mini cart on background click **/
        this.registerClickCloseEvent(".cart-notification__bg");

        /** Close mini cart on X click **/
        this.registerClickCloseEvent(".cart-drawer__inner-real .cart-drawer__close");

        /** Close mini cart on ESC **/
        document.addEventListener("keyup", (event) => {
            if (event.key === "Escape") this.actionCloseMiniCart(event);
        })
    }

    registerClickCloseEvent(className) {
        const miniCartBg = this.eggoCart.querySelector(className);
        if (!miniCartBg) return;
        miniCartBg.addEventListener("click", (e) => this.actionCloseMiniCart(e));
    }

    registerLineItemQuantityChangeEvent() {
        const quantityElements = this.eggoCart.querySelectorAll(".cart-item__quantity .quantity");
        quantityElements.forEach((quantityElement) => {
            quantityElement.addEventListener("change", () => {
                const newQuantity = parseInt(quantityElement.input.value);
                const productId = quantityElement.input.dataset.product;
                this.actionUpdateCartQuantity(productId, newQuantity);
            });
        });
    }

    registerLineItemRemoveEvent() {
        const removeElements = this.eggoCart.querySelectorAll(".cart-item__remove button");
        removeElements.forEach((removeElement) => {
            removeElement.addEventListener("click", () => {
                const productId = removeElement.dataset.product;
                this.actionUpdateCartQuantity(productId, 0);
            });
        });
    }

    registerCouponEvents() {
        const realCart = this.eggoCart.querySelector(".cart-drawer__inner-real");
        if (!realCart) return;

        const toggleButton = realCart.querySelector(".cart-drawer__footer-coupon-toggle");
        if (toggleButton) {
            toggleButton.addEventListener("click", () => {
                hideDomElement(toggleButton);
                this.actionShowCouponField();
            });
        }

        const form = realCart.querySelector(".eggo-discount-form");
        if (form) {
            form.addEventListener("submit", (event) => {
                event.preventDefault();
                this.actionApplyDiscount(form.querySelector(".eggo-discount-form__input").value.trim());
            });
        }

        realCart.querySelectorAll(".eggo-discount-tag__remove").forEach((removeButton) => {
            removeButton.addEventListener("click", () => {
                this.actionRemoveDiscount(removeButton.closest(".eggo-discount-tag").dataset.discountCode);
            });
        });
    }

    registerAddToCartGifts() {
        const buttons = this.eggoCart.querySelectorAll(".is-add-to-cart:not(.eggo-button--buy)");
        if (!buttons) return;
        buttons.forEach((button) => button.addEventListener("click", buttonAddToCart));
    }


    actionCloseMiniCart(e) {
        e.preventDefault();
        this.eggoCart.classList.remove("open");
        documentEventDispatcher(EVENT_SHOW_QUANTITY_UPSELL, {});
    }


    actionShowLoader() {
        this.eggoCart.classList.add("loading")
    }

    actionShowCouponField() {
        const couponFieldElement = this.eggoCart.querySelector(".cart-drawer__inner-real .eggo-discount-input");
        if (couponFieldElement) couponFieldElement.classList.add("visible");
    }

    registerOpenMiniCartEvent() {
        document.addEventListener(EVENT_SHOW_MINI_CART, () => {
            this.eggoCart.classList.add("open");
        });
    }

    registerAddToCartGlobalEvent() {
        document.addEventListener(EVENT_ADD_TO_CART, (event) => {
            if (this.eggoCart) {
                documentEventDispatcher(EVENT_SHOW_MINI_CART);
            } else {
                this.setWrapperLoading();
            }
            this.cartRequest(event.detail.url, event.detail.data);
        });
    }

    /** Cart manipulation **/
    actionUpdateCartQuantity(productId, quantity) {
        this.cartRequest(`${routes.cart_update_url}.js`, {
            updates:
                {
                    [productId]: quantity
                }
        });
    }

    /** Discount codes. /cart/update.js replaces the whole list, so we always send every code that is still applied. **/
    getAppliedCodes() {
        const tags = this.eggoCart.querySelectorAll(".cart-drawer__inner-real .eggo-discount-tag");
        return [...tags].map((tag) => tag.dataset.discountCode);
    }

    hasAppliedCode(code) {
        return this.getAppliedCodes().some((applied) => applied.toLowerCase() === code.toLowerCase());
    }

    actionApplyDiscount(code) {
        if (!code) return;
        const previous = this.getAppliedCodes();
        const codes = this.hasAppliedCode(code) ? previous : [...previous, code];
        this.cartRequest(`${routes.cart_update_url}.js`, {discount: codes.join(",")}, {couponAttempt: {code, previous}});
    }

    actionRemoveDiscount(code) {
        const codes = this.getAppliedCodes().filter((applied) => applied !== code);
        this.cartRequest(`${routes.cart_update_url}.js`, {discount: codes.join(",")});
    }

    /** A code that did not turn into a visible discount is not usable in the cart: take it out again and tell the customer. */
    verifyCouponAttempt(options) {
        if (options.couponError) {
            this.showCouponError(options.couponError);
            return;
        }
        if (!options.couponAttempt) return;
        const {code, previous} = options.couponAttempt;
        if (this.hasAppliedCode(code)) return;
        this.cartRequest(`${routes.cart_update_url}.js`, {discount: previous.join(",")}, {couponError: code});
    }

    showCouponError(code) {
        const realCart = this.eggoCart.querySelector(".cart-drawer__inner-real");
        const inputBlock = realCart?.querySelector(".eggo-discount-input");
        if (!inputBlock) return;
        inputBlock.classList.add("visible");
        hideDomElement(realCart.querySelector(".cart-drawer__footer-coupon-toggle"));
        inputBlock.querySelector(".eggo-discount-form__input").value = code;
        inputBlock.querySelector(".eggo-discount-form__error").hidden = false;
    }

    activateFakeCart = (clearCache = false) => {
        const fakeElement = this.eggoCart.querySelector(".cart-drawer__inner-fake");
        fakeElement.innerHTML = this.cartCache;
        if (clearCache) this.cartCache = null;
    }
    setFakeCart = () => {
        const realElement = this.eggoCart.querySelector(".cart-drawer__inner-real");
        const fakeDom = document.createElement("div");
        fakeDom.innerHTML = realElement.innerHTML;
        this.cartCache = fakeDom.innerHTML;
    }

    cartRequest = (url, body, options = {}) => {
        this.requestQueue = this.requestQueue
            .then(() => this.runCartRequest(url, body, options))
            .catch((error) => console.error(error));
        return this.requestQueue;
    }

    async runCartRequest(url, body, options) {
        this.clearSafetyTimer();
        if (this.eggoCart) {
            this.actionShowLoader();
            this.setFakeCart();
            this.activateFakeCart();
        }
        this.startSafetyTimer();

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...body,
                    sections: "cart-drawer",
                    sections_url: "/cart"
                })
            });
            const data = await response.json();

            /** Show smooth open if cart is closed or not loaded at all **/
            const smoothOpenMiniCart = !this.eggoCart || !this.eggoCart.classList.contains("open");
            const tempDom = document.createElement("div");
            tempDom.innerHTML = await this.getDrawerHtml(data);
            this.cartDrawer = tempDom;
            if (smoothOpenMiniCart) {
                this.init(tempDom.querySelector("#shopify-section-cart-drawer").innerHTML);
                setTimeout(() => {
                    this.eggoCart.classList.add("open");
                }, 1);
            } else {
                const cartWrapper = tempDom.querySelector(".cart-notification-inner-wrapper");
                cartWrapper.classList.add("open");
                this.init(tempDom.querySelector("#shopify-section-cart-drawer").innerHTML);
            }

            this.unsetWrapperLoading();
            this.refreshCart();
            this.clearSafetyTimer();
        } catch (error) {
            console.error(error);
            this.clearSafetyTimer();
            window.location = routes.cart_url;
            return;
        }

        this.finishCartRender(options);
    }

    /** The Sections API answers with the drawer html. If it did not (e.g. add to cart was refused), ask for the current cart. */
    async getDrawerHtml(data) {
        if (data?.sections?.["cart-drawer"]) return data.sections["cart-drawer"];
        const sections = await fetch(`${routes.cart_url}?sections=cart-drawer`).then(response => response.json());
        return sections["cart-drawer"];
    }

    startSafetyTimer() {
        this.timeout = setTimeout(() => {
            window.location = routes.cart_url;
        }, CART_REQUEST_TIMEOUT);
    }

    clearSafetyTimer() {
        if (!this.timeout) return;
        clearTimeout(this.timeout);
        this.timeout = null;
    }

    /** Everything that depends on the cart contents. Runs from the cart html Shopify just rendered, never from a third party. **/
    finishCartRender(options = {}) {
        try {
            this.registerGiftWrapEvents();
            this.removeGiftWrapIfAlone();
            this.verifyCouponAttempt(options);

            const cart = this.getRenderedCart();
            if (!cart || cart.item_count === 0) return;

            const total = cart.total_price / 100;
            new EggoCartShipping(total, this.eggoCart);
            checkOrderLimit(total);

            const discountLogic = new EggoCartDiscountLogic(this.eggoCart, cart);
            const giftsToRemove = discountLogic.findGiftsToRemove();
            if (giftsToRemove.length) {
                this.cartRequest(`${routes.cart_change_url}.js`, {id: giftsToRemove[0], quantity: 0});
            }
        } catch (error) {
            console.error(error);
        }
    }

    getRenderedCart() {
        const script = this.eggoCart.querySelector(".cart-drawer__inner-real script[data-eggo-cart-json]");
        if (!script) return null;
        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            return null;
        }
    }

    removeGiftWrapIfAlone() {
        const giftData = this.getGift();
        if (!giftData?.id) return;
        const cartItems = this.eggoCart.querySelectorAll(`.cart-drawer__inner-real .cart-item:not([data-variant-id="${giftData.id}"])`);
        if (cartItems.length === 0) {
            removeItemCart(giftData.id);
        }
    }

    registerGiftWrapEvents() {
        const giftData = this.getGift();
        if (!giftData.element) return;
        giftData.element.addEventListener("click", () => {
            if (giftData.element.classList.contains("item-is-checked")) {
                removeItemCart(giftData.id);
            } else {
                addToCart([{
                    id: giftData.id,
                    quantity: 1
                }]);
            }
        });
    }

    getGift() {
        const giftWrapper = this.eggoCart.querySelector(".cart-drawer__inner-real .cart-drawer__footer-gift__inner");
        let giftId = null;
        if (giftWrapper) {
            giftId = giftWrapper.dataset?.productId;
        }
        return {
            element: giftWrapper,
            id: giftId
        }
    }
}
